import { BskyAgent, Agent } from '@atproto/api'
import { BrowserOAuthClient } from '@atproto/oauth-client-browser'
import { ref } from 'vue';

// Dynamically detect current origin for local development to avoid localhost vs 127.0.0.1 mismatches
const currentOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://127.0.0.1:5173';
const appUrl = import.meta.env.VITE_APP_URL || currentOrigin;

const encodedRedirect = encodeURIComponent(appUrl)
const encodedScope = encodeURIComponent('atproto transition:generic transition:chat.bsky atproto:did:web:api.bsky.chat')
// Using both transitional scopes and strict audience-bound atproto scope for the chat service.
const loopbackId = `http://localhost/?redirect_uri=${encodedRedirect}&scope=${encodedScope}`

// For production on Netlify, client_id must be https and have a path.
// The @atproto/oauth-client-browser package uses Zod to validate this strictly.
const isLocalhost = appUrl.includes('localhost') || appUrl.includes('127.0.0.1');
const clientId = isLocalhost ? loopbackId : `${appUrl}/client-metadata.json`;

// AppView service URL for general reads and metadata
const BSKY_SERVICE = 'https://bsky.social'
// Public gateway for unauthenticated reads (search, public profiles, etc)
const BSKY_PUBLIC_SERVICE = 'https://api.bsky.app'

export const oauthClient = new BrowserOAuthClient({
    handleResolver: BSKY_SERVICE,
    clientMetadata: {
        client_id: clientId,
        client_name: 'Gamedev Tag OAuth',
        client_uri: appUrl,
        redirect_uris: [appUrl],
        scope: 'atproto transition:generic transition:chat.bsky atproto:did:web:api.bsky.chat',
        grant_types: ['authorization_code', 'refresh_token'],
        response_types: ['code'],
        application_type: 'native',
        token_endpoint_auth_method: 'none',
        dpop_bound_access_tokens: true
    },
})

// Internal state for agents
let _agent: any = new BskyAgent({ service: BSKY_SERVICE })
let _pdsAgent: any = _agent;

// Publicly exported agents (getters ensure we always get the latest authenticated instance)
export const getAgent = () => _agent;
export const getPdsAgent = () => _pdsAgent;

// Backward compatibility for existing imports (standard ESM live bindings)
export { _agent as agent, _pdsAgent as pdsAgent };

export const getPdsEndpoint = async (actor: string): Promise<string | null> => {
    try {
        let did = actor;
        if (!actor.startsWith('did:')) {
            const profile = await publicAgent.getProfile({ actor });
            did = profile.data.did;
        }

        if (did.startsWith('did:plc:')) {
            const response = await fetch(`https://plc.directory/${did}`);
            if (!response.ok) return null;
            const data = await response.json();
            const service = data.service?.find((s: any) =>
                s.id === '#atproto_pds' ||
                s.type === 'AtprotoPersonalDataServer'
            );
            return service?.serviceEndpoint || null;
        } else if (did.startsWith('did:web:')) {
            const domain = did.split(':')[2];
            const response = await fetch(`https://${domain}/.well-known/did.json`);
            if (!response.ok) return null;
            const data = await response.json();
            const service = data.service?.find((s: any) =>
                s.id === '#atproto_pds' ||
                s.type === 'AtprotoPersonalDataServer'
            );
            return service?.serviceEndpoint || null;
        }
    } catch (e) {
        console.error('Failed to resolve PDS for', actor, e);
    }
    return null;
};

// Public agent explicitly for unauthenticated fallback reads
export const publicAgent = new BskyAgent({ service: BSKY_PUBLIC_SERVICE })

// Reactive state
export const bookmarkedUris = ref<Set<string>>(new Set());
export const bookmarkGroups = ref<any[]>([]);
export const currentUser = ref<{ did: string; handle: string } | null>(null);
export const userProfile = ref<any>(null);
export const adminBlocks = ref<Set<string>>(new Set());
export const unreadCount = ref(0);
export const chatUnreadCount = ref(0);
export const lastDeletedPostUri = ref<string | null>(null);

const BSKY_ADMIN = import.meta.env.VITE_BSKY_ADMIN || 'igrir.bsky.social';
let blocksPromise: Promise<void> | null = null;

export const initAdminBlocks = async () => {
    if (blocksPromise) return blocksPromise;
    blocksPromise = (async () => {
        try {
            console.log('[Blocks] Initializing admin blocks. Admin handle:', BSKY_ADMIN);
            // Resolve admin handle to DID
            const profile = await publicAgent.getProfile({ actor: BSKY_ADMIN });
            const adminDid = profile.data.did;
            console.log('[Blocks] Admin DID resolved:', adminDid);

            // Resolve PDS endpoint for listing records
            // api.bsky.app does NOT support listRecords, so we must use a PDS or bsky.social
            const pdsUrl = await getPdsEndpoint(adminDid) || 'https://bsky.social';
            console.log('[Blocks] Using service for retrieval:', pdsUrl);
            const pdsAgent = new BskyAgent({ service: pdsUrl });

            const blocks = new Set<string>();

            // 1. Fetch direct blocks from admin's repo
            try {
                const response = await pdsAgent.com.atproto.repo.listRecords({
                    repo: adminDid,
                    collection: 'app.bsky.graph.block',
                    limit: 100
                });
                console.log('[Blocks] Direct block records count:', response.data.records.length);
                response.data.records.forEach((r: any) => {
                    if (r.value?.subject) blocks.add(r.value.subject);
                });
            } catch (e) {
                console.error('[Blocks] Failed to fetch direct blocks:', e);
            }

            // 2. Fetch list blocks (moderation lists)
            try {
                const listBlocksRes = await pdsAgent.com.atproto.repo.listRecords({
                    repo: adminDid,
                    collection: 'app.bsky.graph.listblock',
                    limit: 100
                });
                console.log('[Blocks] List block records count:', listBlocksRes.data.records.length);
                
                for (const lb of listBlocksRes.data.records) {
                    const listUri = (lb.value as any).subject;
                    if (!listUri) continue;
                    
                    console.log('[Blocks] Fetching members for list:', listUri);
                    // Fetch items for this specific list
                    // Since we can't filter server-side easily by list URI in listRecords,
                    // we fetch all items and filter. Admin repos usually aren't huge.
                    const listItemsRes = await pdsAgent.com.atproto.repo.listRecords({
                        repo: adminDid,
                        collection: 'app.bsky.graph.listitem',
                        limit: 100
                    });
                    
                    const members = listItemsRes.data.records
                        .filter((item: any) => item.value.list === listUri)
                        .map((item: any) => item.value.subject);
                    
                    console.log(`[Blocks] Found ${members.length} members in list ${listUri}`);
                    members.forEach(did => {
                        if (did) blocks.add(did);
                    });
                }
            } catch (e) {
                console.error('[Blocks] Failed to fetch list blocks:', e);
            }

            adminBlocks.value = blocks;
            console.log('[Blocks] Admin blocks initialized total:', blocks.size);
        } catch (e) {
            console.error('[Blocks] Failed to initialize admin blocks:', e);
        }
    })();
    return blocksPromise;
};

export const filterBlockedPosts = async (posts: any[]) => {
    if (blocksPromise) await blocksPromise;
    
    if (!adminBlocks.value || adminBlocks.value.size === 0) {
        console.log('[Blocks] Admin blocklist is empty');
        return posts;
    }
    
    console.log(`[Blocks] Checking ${posts.length} posts against ${adminBlocks.value.size} blocks`);
    
    return posts.filter(item => {
        // Handle both post views (search) and feed items (getListFeed)
        const post = item.post || item;
        
        // Deep extraction of author DID
        let authorDid = '';
        if (post.author) {
            if (typeof post.author === 'string') authorDid = post.author;
            else if (post.author.did) authorDid = post.author.did;
        } else if (post.creator) { // Some list items might use creator
             authorDid = typeof post.creator === 'string' ? post.creator : post.creator.did;
        }

        if (authorDid && adminBlocks.value.has(authorDid)) {
            console.log(`[Blocks] Filtering out post from blocked author: ${authorDid}`);
            return false;
        }
        
        return true;
    });
};

export interface Event {
    uri: string;
    cid: string;
    author: {
        did: string;
        handle: string;
        displayName?: string;
        avatar?: string;
    };
    name: string;
    date: string; // YYYY-MM-DD (Start Date)
    time?: string; // Start Time
    endDate?: string; // YYYY-MM-DD
    endTime?: string;
    place?: string;
    description?: string;
    image?: string;
    rawPost: any;
}

export interface GlobalPrompt {
    show: boolean;
    title: string;
    message: string;
    type: 'alert' | 'confirm';
    confirmText?: string;
    cancelText?: string;
    resolve?: (value: boolean) => void;
}

export const globalPrompt = ref<GlobalPrompt>({
    show: false,
    title: '',
    message: '',
    type: 'alert',
    resolve: undefined
});

export const isFocusMode = ref(false);
let initPromise: Promise<boolean> | null = null;
export const initOAuth = async () => {
    if (initPromise) return initPromise;

    initPromise = (async () => {
        console.log('[OAuth] initOAuth starting.');
        try {
            const result = await oauthClient.init()
            if (result && result.session) {
                console.log('[OAuth] Session found! DID:', result.session.did);

                // Initialize modern Agent with OAuth session natively
                const sessionAgent = new Agent(result.session);

                _agent = sessionAgent;
                _pdsAgent = sessionAgent;

                console.log(`[OAuth] Authenticated. PDS: ${(_pdsAgent as any).pdsUrl?.href || 'resolved automatically'}`);

                currentUser.value = {
                    did: result.session.did,
                    handle: (result.session as any).handle || result.session.did
                };
                await fetchUserProfile()
                return true
            } else {
                console.log('[OAuth] No session found.');
                initPromise = null;
            }
        } catch (e: any) {
            console.error('[OAuth] initialization failed:', e)
            initPromise = null;
        }
        return false
    })();
    return initPromise;
}

export const login = async (identifier: string) => {
    try {
        await oauthClient.signIn(identifier, {
            prompt: 'login',
            scope: 'atproto transition:generic transition:chat.bsky atproto:did:web:api.bsky.chat',
        })
    } catch (error) {
        console.error('Login redirect failed:', error)
        throw error
    }
}

export const logout = async () => {
    if (currentUser.value?.did) {
        try {
            await oauthClient.revoke(currentUser.value.did)
        } catch (error) {
            console.error('Failed to revoke OAuth session:', error)
        }
    }
    localStorage.removeItem('bsky_session')
    _agent = new BskyAgent({ service: 'https://api.bsky.app' })
    _pdsAgent = _agent;
    currentUser.value = null;
    userProfile.value = null;
    location.reload()
}

export const isAuthenticated = () => !!currentUser.value?.did
export const getCurrentUser = () => currentUser.value;

export const searchGamedevPosts = async (tag: string = 'gamedevid', cursor?: string) => {
    return await searchPosts(`#${tag}`, 30, cursor);
}

export const searchEventPosts = async (cursor?: string) => {
    await initOAuth();
    const activeAgent = currentUser.value?.did ? _agent : publicAgent;
    try {
        const response = await activeAgent.app.bsky.feed.searchPosts({
            q: '#gdievent',
            limit: 100,
            cursor
        });
        const posts = await filterBlockedPosts(response.data.posts);
        return { 
            events: posts.map(post => parseEventFromPost(post)).filter(e => !!e) as Event[],
            cursor: response.data.cursor
        };
    } catch (error) {
        console.error('Error searching event posts:', error);
        return { events: [], cursor: undefined };
    }
};

export const parseEventFromPost = (post: any): Event | null => {
    const text = post.record?.text || '';
    if (!text.toLowerCase().includes('#gdievent')) return null;

    const lines = text.split('\n');
    let name = '';
    let date = '';
    let time = '';
    let endDate = '';
    let endTime = '';
    let place = '';
    let description = '';

    // Use regex to extract fields
    // 📢 Event Name
    const nameMatch = text.match(/(?:Event:|📢)[ \t]*([^\n]*)(?=\s*(?:\n|📅|🕒|⏰|🗓️|⏳|📍|$))/i);
    
    // 🗓️ Combined Start Date and Time (e.g. 🗓️ 2026-03-20 19:00 WIB)
    // Supports: 🗓️ YYYY-MM-DD HH:mm ...
    const startMatch = text.match(/🗓️\s*(\d{4}-\d{2}-\d{2})(?:\s+([^\n⏳📍]+))?/);
    
    // ⏳ Combined End Date and Time
    const endMatch = text.match(/⏳\s*(\d{4}-\d{2}-\d{2})(?:\s+([^\n📍]+))?/);

    // Backward compatibility matches
    const dateMatch = text.match(/(?:Date:|📅)\s*(\d{4}-\d{2}-\d{2})/i);
    const timeMatch = text.match(/(?:Time:|🕒|⏰)[ \t]*([^\n]*)(?=\s*(?:\n|📍|$))/i);
    const placeMatch = text.match(/(?:Place:|📍)[ \t]*([^\n]*)(?=\s*(?:\n|$))/i);

    if (nameMatch) name = nameMatch[1].trim();
    
    if (startMatch) {
        date = startMatch[1];
        time = startMatch[2]?.trim() || '';
    } else if (dateMatch) {
        date = dateMatch[1];
        if (timeMatch) time = timeMatch[1].trim();
    }

    if (endMatch) {
        endDate = endMatch[1];
        endTime = endMatch[2]?.trim() || '';
    }

    if (placeMatch) place = placeMatch[1].trim();

    // Fallback for name if not found via prefix
    if (!name) {
        const firstLine = text.split('\n')[0].trim();
        if (firstLine && !firstLine.startsWith('#')) {
            name = firstLine.replace(/^📢\s*/, '').trim();
        }
    }

    // Description fallback: any remaining lines that aren't the primary fields or hashtags
    if (!description) {
        const cleanedLines = text.split('\n').filter((line: string) => {
            const lowerLine = line.toLowerCase();
            return !lowerLine.includes('#gdievent') && 
                   !nameMatch?.[0].includes(line) &&
                   !startMatch?.[0].includes(line) &&
                   !endMatch?.[0].includes(line) &&
                   !dateMatch?.[0].includes(line) &&
                   !timeMatch?.[0].includes(line) &&
                   !placeMatch?.[0].includes(line);
        });
        description = cleanedLines.join(' ').trim();
    }

    if (!name) name = 'Untitled Event';
    
    // Try to normalize date if possible, otherwise keep as is
    // For the schedule view, we'll need a stable date. 
    // If date is missing, use createdAt
    if (!date) {
        date = new Date(post.record?.createdAt).toISOString().split('T')[0];
    }

    let image = '';
    if (post.embed?.images?.[0]?.fullsize) {
        image = post.embed.images[0].fullsize;
    } else if (post.embed?.external?.thumb) {
        image = post.embed.external.thumb;
    }

    return {
        uri: post.uri,
        cid: post.cid,
        author: {
            did: post.author.did,
            handle: post.author.handle,
            displayName: post.author.displayName,
            avatar: post.author.avatar
        },
        name,
        date,
        time,
        endDate,
        endTime,
        place,
        description: description.trim(),
        image,
        rawPost: post
    };
};

export const searchPosts = async (query: string, limit: number = 50, cursor?: string) => {
    await initOAuth();
    const activeAgent = currentUser.value?.did ? _agent : publicAgent
    try {
        const response = await activeAgent.app.bsky.feed.searchPosts({ q: query, limit, cursor })
        return { posts: await filterBlockedPosts(response.data.posts), cursor: response.data.cursor }
    } catch (error) {
        if (activeAgent !== publicAgent) {
            try {
                const res = await publicAgent.app.bsky.feed.searchPosts({ q: query, limit, cursor });
                return { posts: await filterBlockedPosts(res.data.posts), cursor: res.data.cursor };
            } catch (fallbackError) {
                console.error('Error in searchPosts (fallback):', fallbackError)
            }
        }
        return { posts: [], cursor: undefined }
    }
}

export const searchActors = async (term: string, limit: number = 10) => {
    await initOAuth();
    const activeAgent = currentUser.value?.did ? _agent : publicAgent
    try {
        const response = await activeAgent.app.bsky.actor.searchActors({ term, limit })
        return response.data.actors
    } catch (error) {
        if (activeAgent !== publicAgent) {
            try {
                return (await publicAgent.app.bsky.actor.searchActors({ term, limit })).data.actors
            } catch (fallbackError) {
                console.error('Error in searchActors (fallback):', fallbackError)
            }
        }
        return []
    }
}

export const searchActorsTypeahead = async (q: string, limit: number = 10) => {
    await initOAuth();
    const activeAgent = currentUser.value?.did ? _agent : publicAgent
    try {
        const response = await activeAgent.app.bsky.actor.searchActorsTypeahead({ q, limit })
        return response.data.actors
    } catch (error) {
        if (activeAgent !== publicAgent) {
            try {
                return (await publicAgent.app.bsky.actor.searchActorsTypeahead({ q, limit })).data.actors
            } catch (fallbackError) {
                console.error('Error in searchActorsTypeahead (fallback):', fallbackError)
            }
        }
        return []
    }
}

const mergeThreads = (thread1: any, thread2: any): any => {
    if (!thread1) return thread2;
    if (!thread2) return thread1;
    if (thread1.post.uri !== thread2.post.uri) return thread1;

    const merged = { ...thread1 };
    const replies1 = thread1.replies || [];
    const replies2 = thread2.replies || [];

    const replyMap = new Map();
    replies1.forEach((r: any) => { if (r.post) replyMap.set(r.post.uri, r); });
    replies2.forEach((r: any) => {
        if (r.post) {
            if (replyMap.has(r.post.uri)) {
                replyMap.set(r.post.uri, mergeThreads(replyMap.get(r.post.uri), r));
            } else {
                replyMap.set(r.post.uri, r);
            }
        }
    });

    merged.replies = Array.from(replyMap.values());
    merged.replies.sort((a: any, b: any) => {
        const dateA = new Date(a.post?.record?.createdAt || 0).getTime();
        const dateB = new Date(b.post?.record?.createdAt || 0).getTime();
        return dateA - dateB;
    });

    return merged;
};

export const getPostThread = async (uri: string): Promise<any> => {
    await initOAuth();
    
    // Sanitize URI: ensure no @ in the authority if it's based on props.handle
    let cleanUri = uri;
    if (uri.startsWith('at://@')) {
        cleanUri = 'at://' + uri.slice(6);
    }

    // Handle repost URIs: resolve the subject first
    if (cleanUri.includes('/app.bsky.feed.repost/')) {
        try {
            const parts = cleanUri.split('/');
            const repo = parts[2];
            const rkey = parts[4];
            const res = await publicAgent.api.com.atproto.repo.getRecord({
                repo,
                collection: 'app.bsky.feed.repost',
                rkey
            });
            if (res.data.value && (res.data.value as any).subject?.uri) {
                cleanUri = (res.data.value as any).subject.uri;
            }
        } catch (e) {
            console.warn('Failed to resolve repost subject:', e);
        }
    }

    const isAuthed = !!currentUser.value?.did;
    try {
        if (!isAuthed) {
            const res = await publicAgent.app.bsky.feed.getPostThread({ uri: cleanUri, depth: 10 });
            return res.data.thread;
        }

        // Prioritize public agent for speed and to avoid 401 errors from some PDSs
        // on read-only requests for external records.
        const [publicRes, authRes] = await Promise.allSettled([
            publicAgent.app.bsky.feed.getPostThread({ uri: cleanUri, depth: 10 }),
            _agent.app.bsky.feed.getPostThread({ uri: cleanUri, depth: 10 })
        ]);

        const publicThread = publicRes.status === 'fulfilled' ? publicRes.value.data.thread : null;
        const authThread = authRes.status === 'fulfilled' ? authRes.value.data.thread : null;

        if (!publicThread && !authThread) {
            // If still not found, check if it was mislabeled as a post but is actually a repost
            if (cleanUri.includes('/app.bsky.feed.post/')) {
                try {
                    const parts = cleanUri.split('/');
                    const repo = parts[2];
                    const rkey = parts[4];
                    const res = await publicAgent.api.com.atproto.repo.getRecord({
                        repo,
                        collection: 'app.bsky.feed.repost',
                        rkey
                    });
                    if (res.data.value && (res.data.value as any).subject?.uri) {
                        return getPostThread((res.data.value as any).subject.uri);
                    }
                } catch (e) {}
            }
            return null;
        }
        
        // Prefer authThread if it exists for extra metadata, but fallback to public
        return mergeThreads(authThread || publicThread, publicThread || authThread);
    } catch (error) {
        console.error('Error fetching thread:', error);
        return null;
    }
};

export const sendReply = async (text: string, root: { uri: string, cid: string }, parent: { uri: string, cid: string }, embed?: any) => {
    try {
        const facets = await generateFacets(text);
        return await _pdsAgent.post({
            text,
            facets: facets.length > 0 ? facets : undefined,
            reply: { root, parent },
            embed,
            langs: ['en'],
            createdAt: new Date().toISOString(),
        })
    } catch (error) {
        console.error('Error sending reply:', error)
        throw error
    }
}

export const uploadBlob = async (blob: Blob, encoding: string) => {
    try {
        const response = await _pdsAgent.uploadBlob(blob, { encoding });
        return response.data.blob;
    } catch (error) {
        console.error('Error uploading blob:', error);
        throw error;
    }
};

/**
 * Uploads a video/GIF through Bluesky's video processing service (recommended method).
 * https://docs.bsky.app/docs/tutorials/video
 */
export const uploadVideo = async (
    videoBlob: Blob,
    onProgress?: (label: string, percent: number) => void
): Promise<any> => {
    const did = currentUser.value?.did;
    if (!did) throw new Error('Not logged in');
    if (videoBlob.size === 0) throw new Error('File is empty.');

    const mimeType = videoBlob.type;
    const ext = mimeType === 'image/gif' ? 'animation.gif'
        : mimeType === 'video/mp4' ? 'video.mp4'
        : 'animation.webm';

    // Step 1: service auth token — aud is the PDS host, lxm is uploadBlob
    onProgress?.('Requesting upload token...', 5);
    let token: string;
    try {
        // _pdsAgent.dispatchUrl gives the PDS URL (pdsUrl || serviceUrl)
        const dispatchUrl: URL = (_pdsAgent as any).dispatchUrl;
        const pdsHost = dispatchUrl?.host || 'bsky.social';
        const tokenRes = await _pdsAgent.com.atproto.server.getServiceAuth({
            aud: `did:web:${pdsHost}`,
            lxm: 'com.atproto.repo.uploadBlob',
            exp: Math.floor(Date.now() / 1000) + 60 * 30,
        });
        token = tokenRes.data.token;
        // Decode token payload (JWT middle section) to inspect claims
        try {
            const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
            console.log('[uploadVideo] token payload:', JSON.stringify(payload));
        } catch { /* ignore decode errors */ }
    } catch (e: any) {
        throw new Error(`Could not get upload token: ${e?.message ?? e}`);
    }

    // Step 2: upload directly to video.bsky.app using plain Bearer token (no DPoP)
    const VIDEO_SERVICE = 'https://video.bsky.app';
    const uploadUrl = new URL(`${VIDEO_SERVICE}/xrpc/app.bsky.video.uploadVideo`);
    uploadUrl.searchParams.set('did', did);
    uploadUrl.searchParams.set('name', ext);

    onProgress?.('Uploading...', 10);
    let uploadData: any;

    // Use XHR instead of fetch — XHR can read the actual HTTP response even when the server
    // sends an error early (while the body is still uploading), unlike fetch which converts
    // server-initiated RST_STREAM into an opaque ERR_HTTP2_PROTOCOL_ERROR.
    const xhrResult = await new Promise<{ status: number; text: string; ok: boolean }>((resolve) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', uploadUrl.toString());
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.setRequestHeader('Content-Type', mimeType);
        xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText, ok: xhr.status >= 200 && xhr.status < 300 });
        xhr.onerror = () => resolve({ status: xhr.status, text: `XHR network error (status ${xhr.status})`, ok: false });
        xhr.send(videoBlob);
    });
    console.log('[uploadVideo] XHR status:', xhrResult.status, 'body:', xhrResult.text.slice(0, 500));
    if (!xhrResult.ok) throw new Error(`Upload failed (${xhrResult.status}): ${xhrResult.text}`);
    uploadData = JSON.parse(xhrResult.text);

    // If already processed, blob is returned immediately
    if (uploadData?.blob) {
        onProgress?.('Done!', 100);
        return uploadData.blob;
    }

    // Step 3: poll getJobStatus until complete
    const jobId: string = uploadData?.jobId ?? uploadData?.job_id;
    if (!jobId) throw new Error('No job ID returned from video upload');

    onProgress?.('Processing video...', 30);
    const pollInterval = 1000;
    const maxWait = 180_000;
    const started = Date.now();

    while (Date.now() - started < maxWait) {
        await new Promise(r => setTimeout(r, pollInterval));
        const statusRes = await fetch(
            `${VIDEO_SERVICE}/xrpc/app.bsky.video.getJobStatus?jobId=${encodeURIComponent(jobId)}`
        );
        if (!statusRes.ok) continue;
        const status = await statusRes.json();
        const job = status?.jobStatus ?? status;
        const pct: number = typeof job?.progress === 'number' ? job.progress : 0;

        if (job?.blob) {
            onProgress?.('Done!', 100);
            return job.blob;
        }
        if (job?.state === 'JOB_STATE_FAILED') {
            if (job?.error === 'already_exists' && job?.blob) {
                onProgress?.('Done!', 100);
                return job.blob;
            }
            throw new Error(`Video processing failed: ${job?.error ?? 'unknown error'}`);
        }
        onProgress?.('Processing video...', 30 + Math.round(pct * 0.65));
    }

    throw new Error('Video processing timed out. Please try again.');
};

export const generateFacets = async (text: string) => {
    const facets: any[] = [];
    const utf8Encoder = new TextEncoder();
    const mentionRegex = /(^|\s)@([a-zA-Z0-9.-]*[a-zA-Z0-9])/g;
    let match;
    while ((match = mentionRegex.exec(text)) !== null) {
        const fullMatch = match[0];
        const handle = match[2];
        const start = match.index + (fullMatch.startsWith('@') ? 0 : 1);
        const end = start + handle.length + 1;
        const byteStart = utf8Encoder.encode(text.substring(0, start)).length;
        const byteEnd = utf8Encoder.encode(text.substring(0, end)).length;
        try {
            const profile = await getOtherProfile(handle);
            if (profile) {
                facets.push({
                    index: { byteStart, byteEnd },
                    features: [{ $type: 'app.bsky.richtext.facet#mention', did: profile.did }]
                });
            }
        } catch (e) {}
    }

    const hashtagRegex = /#(\w+)/g;
    while ((match = hashtagRegex.exec(text)) !== null) {
        const start = match.index;
        const end = start + match[0].length;
        const byteStart = utf8Encoder.encode(text.substring(0, start)).length;
        const byteEnd = utf8Encoder.encode(text.substring(0, end)).length;
        facets.push({
            index: { byteStart, byteEnd },
            features: [{ $type: 'app.bsky.richtext.facet#tag', tag: match[1] }]
        });
    }

    const urlRegex = /https?:\/\/[^\s\n\r\t]+[^\s\n\r\t.,;:!?"')\]]/g;
    while ((match = urlRegex.exec(text)) !== null) {
        const start = match.index;
        const end = start + match[0].length;
        const byteStart = utf8Encoder.encode(text.substring(0, start)).length;
        const byteEnd = utf8Encoder.encode(text.substring(0, end)).length;
        facets.push({
            index: { byteStart, byteEnd },
            features: [{ $type: 'app.bsky.richtext.facet#link', uri: match[0] }]
        });
    }

    return facets.sort((a, b) => a.index.byteStart - b.index.byteStart);
};

export interface InteractionSettings {
    replyOption: 'anyone' | 'nobody' | 'custom';
    replyRules?: {
        followers?: boolean;
        following?: boolean;
        mentioned?: boolean;
        lists?: string[];
    };
    allowQuotes: boolean;
}

export const createPost = async (text: string, embed?: any, settings?: InteractionSettings) => {
    if (!currentUser.value?.did) throw new Error('Not logged in');
    try {
        const facets = await generateFacets(text);
        const response = await _pdsAgent.post({
            text,
            facets: facets.length > 0 ? facets : undefined,
            embed,
            langs: ['en'],
            createdAt: new Date().toISOString(),
        });
        if (settings && (settings.replyOption !== 'anyone' || !settings.allowQuotes)) {
            await applyInteractionSettings(response.uri, settings);
        }
        return response;
    } catch (error) {
        console.error('Error creating post:', error);
        throw error;
    }
};

const applyInteractionSettings = async (postUri: string, settings: InteractionSettings) => {
    if (!currentUser.value?.did) return;
    const rkey = postUri.split('/').pop();
    if (settings.replyOption !== 'anyone') {
        const allow: any[] = [];
        if (settings.replyOption === 'custom' && settings.replyRules) {
            if (settings.replyRules.mentioned) allow.push({ $type: 'app.bsky.feed.threadgate#mentionedRule' });
            if (settings.replyRules.following) allow.push({ $type: 'app.bsky.feed.threadgate#followingRule' });
            if (settings.replyRules.followers) allow.push({ $type: 'app.bsky.feed.threadgate#followerRule' });
            if (settings.replyRules.lists) {
                settings.replyRules.lists.forEach(l => { allow.push({ $type: 'app.bsky.feed.threadgate#listRule', list: l }); });
            }
        }
        try {
            await _pdsAgent.com.atproto.repo.putRecord({
                repo: currentUser.value.did,
                collection: 'app.bsky.feed.threadgate',
                rkey: rkey!,
                record: { post: postUri, allow: allow, createdAt: new Date().toISOString() }
            });
        } catch (e) {}
    }
    if (!settings.allowQuotes) {
        try {
            await _pdsAgent.com.atproto.repo.putRecord({
                repo: currentUser.value.did,
                collection: 'app.bsky.feed.postgate',
                rkey: rkey!,
                record: { post: postUri, embeddingRules: [{ $type: 'app.bsky.feed.postgate#disableRule' }], createdAt: new Date().toISOString() }
            });
        } catch (e) {}
    }
};

export const deletePost = async (uri: string) => {
    try {
        const parts = uri.split('/');
        const repo = parts[2];
        const collection = parts[3];
        const rkey = parts[4];
        await _pdsAgent.com.atproto.repo.deleteRecord({ repo, collection, rkey });
    } catch (error) {
        console.error('Error deleting post:', error);
        throw error;
    }
}

export const likePost = async (uri: string, cid: string) => {
    try { return await _pdsAgent.like(uri, cid); } catch (error) { throw error; }
};

export const unlikePost = async (likeUri: string) => {
    try { await _pdsAgent.deleteLike(likeUri); } catch (error) { throw error; }
};

export const repostPost = async (uri: string, cid: string) => {
    try { return await _pdsAgent.repost(uri, cid); } catch (error) { throw error; }
};

export const unrepostPost = async (repostUri: string) => {
    try { await _pdsAgent.deleteRepost(repostUri); } catch (error) { throw error; }
};

export const getNotifications = async (limit = 30, cursor?: string) => {
    await initOAuth();
    if (!currentUser.value?.did) return { notifications: [], cursor: undefined };
    try {
        const response = await _agent.app.bsky.notification.listNotifications({ limit, cursor });
        return { notifications: response.data.notifications, cursor: response.data.cursor };
    } catch (error) {
        console.error('Error fetching notifications:', error);
        return { notifications: [], cursor: undefined };
    }
};

export const getUnreadCount = async () => {
    await initOAuth();
    if (!currentUser.value?.did) return 0;
    try {
        const response = await _agent.app.bsky.notification.getUnreadCount();
        const count = response.data.count;
        console.log('[Notifications] Unread count fetched:', count);
        unreadCount.value = count;
        return count;
    } catch (error) { 
        console.error('[Notifications] Failed to fetch unread count:', error);
        return 0; 
    }
};

export const updateNotificationsSeen = async () => {
    await initOAuth();
    if (!currentUser.value?.did) return;
    try {
        await _agent.app.bsky.notification.updateSeen({ seenAt: new Date().toISOString() });
        unreadCount.value = 0;
        console.log('[Notifications] Marked all as seen, unread count reset to 0');
    } catch (error) {
        console.error('[Notifications] Failed to update seen status:', error);
    }
};

export const fetchUserProfile = async () => {
    if (!currentUser.value?.did) { userProfile.value = null; return; }
    try {
        const response = await _agent.getProfile({ actor: currentUser.value.did });
        userProfile.value = response.data;
        if (currentUser.value) currentUser.value.handle = response.data.handle;
    } catch (error) {
        try {
            const response = await publicAgent.getProfile({ actor: currentUser.value.did });
            userProfile.value = response.data;
            if (currentUser.value) currentUser.value.handle = response.data.handle;
        } catch (fe) {}
    }
};

export const updateProfile = async (updates: { displayName?: string; description?: string; avatar?: any; banner?: any; }) => {
    if (!currentUser.value?.did) throw new Error('Not logged in');
    try {
        let currentRecord: any = { $type: 'app.bsky.actor.profile' };
        try {
            const res = await _pdsAgent.com.atproto.repo.getRecord({ repo: currentUser.value.did, collection: 'app.bsky.actor.profile', rkey: 'self' });
            currentRecord = res.data.value;
        } catch (e) {}
        await _pdsAgent.com.atproto.repo.putRecord({
            repo: currentUser.value.did,
            collection: 'app.bsky.actor.profile',
            rkey: 'self',
            record: {
                ...currentRecord,
                displayName: updates.displayName !== undefined ? updates.displayName : currentRecord.displayName,
                description: updates.description !== undefined ? updates.description : currentRecord.description,
                avatar: updates.avatar !== undefined ? updates.avatar : currentRecord.avatar,
                banner: updates.banner !== undefined ? updates.banner : currentRecord.banner,
            },
        });
        await fetchUserProfile();
    } catch (error) { throw error; }
};

export const getProfilesBatch = async (dids: string[]): Promise<Record<string, any>> => {
    await initOAuth();
    const activeAgent = currentUser.value?.did ? _agent : publicAgent;
    const result: Record<string, any> = {};
    for (let i = 0; i < dids.length; i += 25) {
        const chunk = dids.slice(i, i + 25);
        try {
            const response = await activeAgent.app.bsky.actor.getProfiles({ actors: chunk });
            for (const p of response.data.profiles) {
                result[p.did] = p;
            }
        } catch (e) {
            console.error('[getProfilesBatch] chunk failed:', e);
        }
    }
    return result;
};

export const getOtherProfile = async (did: string) => {
    await initOAuth();
    const activeAgent = currentUser.value?.did ? _agent : publicAgent;
    try {
        const response = await activeAgent.getProfile({ actor: did });
        return response.data;
    } catch (error) {
        if (activeAgent !== publicAgent) {
            try { return (await publicAgent.getProfile({ actor: did })).data; } catch (fe) {}
        }
        return null;
    }
};

export const followUser = async (actor: string) => {
    await initOAuth();
    try {
        let did = actor;
        if (!actor.startsWith('did:')) {
            const profile = await getOtherProfile(actor);
            if (!profile) throw new Error(`Could not resolve handle ${actor} to DID`);
            did = profile.did;
        }
        return await _pdsAgent.follow(did);
    } catch (error) { throw error; }
};

export const unfollowUser = async (followUri: string) => {
    await initOAuth();
    try { await _pdsAgent.deleteFollow(followUri); } catch (error) { throw error; }
};

export const getBookmarks = async (targetDid?: string) => {
    await initOAuth();
    const did = targetDid || currentUser.value?.did;
    if (!did) return null;
    const isMe = did === currentUser.value?.did;
    const pdsUrl = isMe ? null : await getPdsEndpoint(did);
    const activeAgent = isMe ? _agent : (pdsUrl ? new BskyAgent({ service: pdsUrl }) : _agent);
    try {
        const response = await activeAgent.com.atproto.repo.getRecord({ repo: did, collection: 'id.gamedev.bookmarks', rkey: 'self' });
        const data = response.data.value as any;
        if (!did || (currentUser.value && did === currentUser.value.did)) {
            const uris = new Set<string>();
            data.groups?.forEach((g: any) => { g.postUris?.forEach((u: string) => uris.add(u)); });
            bookmarkedUris.value = uris;
            bookmarkGroups.value = data.groups || [];
        }
        return data;
    } catch (e: any) {
        if (activeAgent !== _agent) {
            try {
                const response = await _agent.com.atproto.repo.getRecord({ repo: did, collection: 'id.gamedev.bookmarks', rkey: 'self' });
                return response.data.value as any;
            } catch (fe) {}
        }
        try {
            return (await publicAgent.com.atproto.repo.getRecord({ repo: did, collection: 'id.gamedev.bookmarks', rkey: 'self' })).data.value;
        } catch (fe) {}
        if (e.status === 400 || e.status === 404 || e.error === 'RecordNotFound') {
            if (did === currentUser.value?.did) bookmarkedUris.value = new Set();
            return { groups: [], updatedAt: new Date().toISOString() };
        }
        return null;
    }
};

export const saveBookmarks = async (bookmarks: any) => {
    if (!currentUser.value?.did) throw new Error('Not logged in');
    try {
        await _pdsAgent.com.atproto.repo.putRecord({
            repo: currentUser.value.did,
            collection: 'id.gamedev.bookmarks',
            rkey: 'self',
            record: { $type: 'id.gamedev.bookmarks', groups: bookmarks.groups, updatedAt: new Date().toISOString() },
        });

        // Update reactive state
        bookmarkGroups.value = bookmarks.groups || [];
        const uris = new Set<string>();
        bookmarks.groups?.forEach((g: any) => { g.postUris?.forEach((u: string) => uris.add(u)); });
        bookmarkedUris.value = uris;
    } catch (e) { throw e; }
};

export const getLikes = async (uri: string, limit: number = 20, cursor?: string) => {
    await initOAuth();
    const activeAgent = currentUser.value?.did ? _agent : publicAgent;
    try {
        const response = await activeAgent.app.bsky.feed.getLikes({ uri, limit, cursor });
        return { likes: response.data.likes, cursor: response.data.cursor };
    } catch (e) { return { likes: [], cursor: undefined }; }
};

export const getRepostedBy = async (uri: string, limit: number = 20, cursor?: string) => {
    await initOAuth();
    const activeAgent = currentUser.value?.did ? _agent : publicAgent;
    try {
        const response = await activeAgent.app.bsky.feed.getRepostedBy({ uri, limit, cursor });
        return { repostedBy: response.data.repostedBy, cursor: response.data.cursor };
    } catch (e) { return { repostedBy: [], cursor: undefined }; }
};

export const getQuotes = async (uri: string, limit: number = 20, cursor?: string) => {
    await initOAuth();
    const activeAgent = currentUser.value?.did ? _agent : publicAgent;
    try {
        const response = await activeAgent.app.bsky.feed.getQuotes({ uri, limit, cursor });
        return { posts: response.data.posts, cursor: response.data.cursor };
    } catch (e) { return { posts: [], cursor: undefined }; }
};

export const getFollowers = async (actor: string, limit: number = 20, cursor?: string) => {
    await initOAuth();
    const activeAgent = currentUser.value?.did ? _agent : publicAgent;
    try {
        const response = await activeAgent.app.bsky.graph.getFollowers({ actor, limit, cursor });
        return { followers: response.data.followers, cursor: response.data.cursor };
    } catch (e) { return { followers: [], cursor: undefined }; }
};

export const getFollows = async (actor: string, limit: number = 20, cursor?: string) => {
    await initOAuth();
    const activeAgent = currentUser.value?.did ? _agent : publicAgent;
    try {
        const response = await activeAgent.app.bsky.graph.getFollows({ actor, limit, cursor });
        return { follows: response.data.follows, cursor: response.data.cursor };
    } catch (e) { return { follows: [], cursor: undefined }; }
};

export const getMyLists = async () => {
    if (!currentUser.value?.did) return [];
    try {
        const response = await _agent.app.bsky.graph.getLists({ actor: currentUser.value.did });
        return response.data.lists;
    } catch (e) { return []; }
};

export const getPostsBatch = async (uris: string[]) => {
    if (uris.length === 0) return [];
    
    // Split URIs by collection
    const postUris = uris.filter(u => u.includes('app.bsky.feed.post'));
    const noteUris = uris.filter(u => u.includes('id.gamedev.notes'));
    
    let allResults: any[] = [];
    
    // Fetch standard posts in batch
    if (postUris.length > 0) {
        try {
            const response = await _agent.app.bsky.feed.getPosts({ uris: postUris });
            allResults = [...allResults, ...response.data.posts];
        } catch (e) {
            try { 
                const pubResponse = await publicAgent.app.bsky.feed.getPosts({ uris: postUris });
                allResults = [...allResults, ...pubResponse.data.posts];
            } catch (fe) {}
        }
    }
    
    // Fetch gamedev notes (requires individual getRecord calls)
    if (noteUris.length > 0) {
        const notePromises = noteUris.map(async (uri) => {
            try {
                const parts = uri.replace('at://', '').split('/');
                const repo = parts[0];
                const rkey = parts[2];
                
                // Construct a author profile (minimal or full)
                let profile: any = { handle: repo, did: repo.startsWith('did:') ? repo : '' };
                try {
                    const prof = await getOtherProfile(repo);
                    if (prof) profile = prof;
                } catch (e) {}
                
                // Use a PDS-aware agent if possible, or standard agent
                const pdsUrl = await getPdsEndpoint(profile.did || repo);
                const activeAgent = pdsUrl ? new BskyAgent({ service: pdsUrl }) : _agent;
                
                const response = await activeAgent.com.atproto.repo.getRecord({
                    repo,
                    collection: 'id.gamedev.notes',
                    rkey
                });

                return {
                    uri,
                    cid: response.data.cid,
                    author: profile,
                    record: response.data.value,
                    indexedAt: (response.data.value as any).createdAt,
                    $type: 'id.gamedev.note' // Internal flag for rendering
                };
            } catch (e) {
                console.warn('[bluesky.ts] Failed to fetch note in batch:', uri, e);
                return null;
            }
        });
        
        const noteResults = (await Promise.all(notePromises)).filter(n => n !== null);
        allResults = [...allResults, ...noteResults];
    }
    
    return allResults;
};

export const getAuthorFeed = async (actor: string, limit: number = 100, cursor?: string) => {
    await initOAuth();
    const activeAgent = currentUser.value?.did ? _agent : publicAgent;
    try {
        const response = await activeAgent.app.bsky.feed.getAuthorFeed({ actor, limit, cursor });
        return { feed: await filterBlockedPosts(response.data.feed), cursor: response.data.cursor };
    } catch (error) {
        if (activeAgent !== publicAgent) {
            try {
                const res = await publicAgent.app.bsky.feed.getAuthorFeed({ actor, limit, cursor });
                return { feed: await filterBlockedPosts(res.data.feed), cursor: res.data.cursor };
            } catch (fe) { }
        }
        return { feed: [], cursor: undefined };
    }
};

export interface CustomDiscover { id?: string; name: string; query: string; uri?: string; rkey?: string; }
export const customDiscovers = ref<CustomDiscover[]>([]);

export const getDiscovers = async (targetDid?: string) => {
    await initOAuth();
    const did = targetDid || currentUser.value?.did;
    if (!did) return [];
    
    try {
        const pdsUrl = await getPdsEndpoint(did);
        const activeAgent = pdsUrl ? new BskyAgent({ service: pdsUrl }) : _agent;
        
        const response = await activeAgent.com.atproto.repo.listRecords({
            repo: did,
            collection: 'id.gamedev.discovers',
            limit: 100
        });
        
        const discovers = response.data.records.map((r: any) => ({
            ...r.value,
            uri: r.uri,
            rkey: r.uri.split('/').pop()
        }));
        
        if (did === currentUser.value?.did) customDiscovers.value = discovers;
        return discovers;
    } catch (e: any) {
        console.error('[bluesky.ts] Failed to fetch discovers:', e);
        if (did === currentUser.value?.did) customDiscovers.value = [];
        return [];
    }
};

export const saveDiscover = async (discover: CustomDiscover) => {
    if (!currentUser.value?.did) throw new Error('Not logged in');
    try {
        const record = {
            $type: 'id.gamedev.discovers',
            name: discover.name,
            query: discover.query,
            updatedAt: new Date().toISOString()
        };

        if (discover.rkey) {
            await _pdsAgent.com.atproto.repo.putRecord({
                repo: currentUser.value.did,
                collection: 'id.gamedev.discovers',
                rkey: discover.rkey,
                record
            });
        } else {
            await _pdsAgent.com.atproto.repo.createRecord({
                repo: currentUser.value.did,
                collection: 'id.gamedev.discovers',
                record
            });
        }
        await getDiscovers();
    } catch (e) { throw e; }
};

export const deleteDiscover = async (uri: string) => {
    if (!currentUser.value?.did) throw new Error('Not logged in');
    try {
        const rkey = uri.split('/').pop();
        if (!rkey) throw new Error('Invalid discovery URI');
        
        await _pdsAgent.com.atproto.repo.deleteRecord({
            repo: currentUser.value.did,
            collection: 'id.gamedev.discovers',
            rkey
        });
        await getDiscovers();
    } catch (e) { throw e; }
};

export interface PortfolioSection { type: 'h1' | 'h2' | 'text' | 'image'; content: string; blob?: any; pendingFile?: File; }
export interface Experience { company: string; position: string; startDate: string; endDate?: string; current: boolean; description: string; }
export interface GameWork { title: string; role: string; year: string; platform: string; link?: string; thumbnail?: string; blob?: any; pendingFile?: File; _uploadType?: 'url' | 'file'; searching?: boolean; }
export interface SocialLink { platform: string; url: string; }
export interface Portfolio { bio: string; sections: PortfolioSection[]; experiences: Experience[]; ludography: GameWork[]; socials: SocialLink[]; contact: { email?: string; website?: string; }; updatedAt: string; }

export const getPortfolio = async (targetDid?: string): Promise<Portfolio | null> => {
    await initOAuth();
    const did = targetDid || currentUser.value?.did;
    if (!did) return null;
    const pdsUrl = await getPdsEndpoint(did);
    const activeAgent = pdsUrl ? new BskyAgent({ service: pdsUrl }) : _agent;
    try {
        const response = await activeAgent.com.atproto.repo.getRecord({ repo: did, collection: 'id.gamedev.profile', rkey: 'self' });
        return response.data.value as unknown as Portfolio;
    } catch (e: any) {
        if (activeAgent !== _agent) {
            try {
                const response = await _agent.com.atproto.repo.getRecord({ repo: did, collection: 'id.gamedev.profile', rkey: 'self' });
                return response.data.value as unknown as Portfolio;
            } catch (fe) {}
        }
        try {
            return (await publicAgent.com.atproto.repo.getRecord({ repo: did, collection: 'id.gamedev.profile', rkey: 'self' })).data.value as unknown as Portfolio;
        } catch (fe) {}
        return null;
    }
};

export const savePortfolio = async (portfolio: Portfolio) => {
    await initOAuth();
    if (!currentUser.value?.did) throw new Error('Not logged in');
    try {
        await _pdsAgent.com.atproto.repo.putRecord({
            repo: currentUser.value.did,
            collection: 'id.gamedev.profile',
            rkey: 'self',
            record: { $type: 'id.gamedev.profile', ...portfolio, updatedAt: new Date().toISOString() },
        });
    } catch (e) { throw e; }
};

export const listConversations = async (limit = 30, cursor?: string) => {
    await initOAuth();
    if (!currentUser.value?.did) return { convos: [], cursor: undefined };
    try {
        const response = await _agent.api.chat.bsky.convo.listConvos({ limit, cursor }, { headers: { 'atproto-proxy': 'did:web:api.bsky.chat#bsky_chat' } });
        return { convos: response.data.convos, cursor: response.data.cursor };
    } catch (error) { return { convos: [], cursor: undefined }; }
};

export const getConversationMessages = async (convoId: string, limit = 50, cursor?: string) => {
    await initOAuth();
    if (!currentUser.value?.did) return { messages: [], cursor: undefined };
    try {
        const response = await _agent.api.chat.bsky.convo.getMessages({ convoId, limit, cursor }, { headers: { 'atproto-proxy': 'did:web:api.bsky.chat#bsky_chat' } });
        return { messages: response.data.messages, cursor: response.data.cursor };
    } catch (error) { return { messages: [], cursor: undefined }; }
};

export const sendMessageToConvo = async (convoId: string, text: string) => {
    await initOAuth();
    if (!currentUser.value?.did) throw new Error('Not logged in');
    try {
        const facets = await generateFacets(text);
        return await _agent.api.chat.bsky.convo.sendMessage({ convoId, message: { text, facets: facets.length > 0 ? facets : undefined } }, { headers: { 'atproto-proxy': 'did:web:api.bsky.chat#bsky_chat' } });
    } catch (error) { throw error; }
};

export const getUnreadChatCount = async () => {
    try {
        const { convos } = await listConversations(20);
        const count = convos.reduce((acc: number, c: any) => acc + (c.unreadCount || 0), 0);
        chatUnreadCount.value = count;
        return count;
    } catch (e) { return 0; }
};

export const getOrCreateConvo = async (members: string[]) => {
    await initOAuth();
    if (!currentUser.value?.did) throw new Error('Not logged in');
    try {
        const response = await _agent.api.chat.bsky.convo.getConvoForMembers({ members: [currentUser.value.did, ...members] }, { headers: { 'atproto-proxy': 'did:web:api.bsky.chat#bsky_chat' } });
        return response.data.convo;
    } catch (error) { throw error; }
};

export const updateChatRead = async (convoId: string, messageId?: string) => {
    await initOAuth();
    if (!currentUser.value?.did) return;
    try { await _agent.api.chat.bsky.convo.updateRead({ convoId, messageId }, { headers: { 'atproto-proxy': 'did:web:api.bsky.chat#bsky_chat' } }); } catch (error) {}
};

export const getUserLists = async (actorDid?: string, limit = 50, cursor?: string) => {
    await initOAuth();
    const did = actorDid || currentUser.value?.did;
    if (!did) return { lists: [], cursor: undefined };
    try {
        const response = await _agent.app.bsky.graph.getLists({ actor: did, limit, cursor });
        return { lists: response.data.lists, cursor: response.data.cursor };
    } catch (error) { return { lists: [], cursor: undefined }; }
};

export const getListMembers = async (listUri: string, limit = 50, cursor?: string) => {
    await initOAuth();
    const activeAgent = currentUser.value?.did ? _agent : publicAgent;
    try {
        const response = await activeAgent.app.bsky.graph.getList({ list: listUri, limit, cursor });
        return { list: response.data.list, items: response.data.items, cursor: response.data.cursor };
    } catch (error) { return { list: null, items: [], cursor: undefined }; }
};

export const createNativeList = async (name: string, description: string = '', purpose: string = 'app.bsky.graph.defs#curatelist') => {
    if (!currentUser.value?.did) throw new Error('Not logged in');
    try {
        return await _pdsAgent.com.atproto.repo.createRecord({
            repo: currentUser.value.did,
            collection: 'app.bsky.graph.list',
            record: { $type: 'app.bsky.graph.list', name, description, purpose, createdAt: new Date().toISOString() },
        });
    } catch (error) { throw error; }
};

export const addActorToList = async (listUri: string, actorDid: string) => {
    if (!currentUser.value?.did) throw new Error('Not logged in');
    try {
        return await _pdsAgent.com.atproto.repo.createRecord({
            repo: currentUser.value.did,
            collection: 'app.bsky.graph.listitem',
            record: { $type: 'app.bsky.graph.listitem', subject: actorDid, list: listUri, createdAt: new Date().toISOString() },
        });
    } catch (error) { throw error; }
};

export const removeActorFromList = async (itemUri: string) => {
    if (!currentUser.value?.did) throw new Error('Not logged in');
    try {
        const parts = itemUri.split('/');
        const rkey = parts[parts.length - 1];
        return await _pdsAgent.com.atproto.repo.deleteRecord({ repo: currentUser.value.did, collection: 'app.bsky.graph.listitem', rkey });
    } catch (error) { throw error; }
};

export const getListFeed = async (listUri: string, limit = 100, cursor?: string) => {
    await initOAuth();
    const activeAgent = currentUser.value?.did ? _agent : publicAgent;
    try {
        const response = await activeAgent.app.bsky.feed.getListFeed({ list: listUri, limit, cursor });
        return { feed: await filterBlockedPosts(response.data.feed), cursor: response.data.cursor };
    } catch (error) {
        if (activeAgent !== publicAgent) {
            try {
                const response = await publicAgent.app.bsky.feed.getListFeed({ list: listUri, limit, cursor });
                return { feed: await filterBlockedPosts(response.data.feed), cursor: response.data.cursor };
            } catch (fallbackError) {
                console.error('Error fetching list feed (fallback):', fallbackError);
            }
        }
        return { feed: [], cursor: undefined };
    }
};

export const updateNativeList = async (listUri: string, updates: { name: string; description?: string; avatar?: any }) => {
    if (!currentUser.value?.did) throw new Error('Not logged in');
    try {
        return await _pdsAgent.com.atproto.repo.putRecord({
            repo: currentUser.value.did,
            collection: 'app.bsky.graph.list',
            rkey: listUri.split('/').pop()!,
            record: {
                $type: 'app.bsky.graph.list',
                name: updates.name,
                description: updates.description || '',
                purpose: 'app.bsky.graph.defs#curatelist',
                avatar: updates.avatar,
                createdAt: new Date().toISOString(),
            },
        });
    } catch (error) { throw error; }
};

// ── Studios (id.gamedev.studios) ──────────────────────────────────────────────

export interface Studio {
    uri?: string;
    cid?: string;
    rkey?: string;
    name: string;
    handle?: string;
    website?: string;
    location?: string;
    description?: string;
    logoUrl?: string;
    logoBlob?: any;
    banner?: string;
    descriptionFacets?: any[];
    tags?: string[];
    foundedYear?: string;
    createdAt: string;
    updatedAt?: string;
}

export const resolveStudioLogoUrl = (studio: Studio, adminDid: string): string => {
    if (studio.logoBlob) {
        const cid = studio.logoBlob.ref?.$link || studio.logoBlob.ref?.toString() || studio.logoBlob.cid;
        if (cid && adminDid) {
            const ext = studio.logoBlob.mimeType?.split('/').pop() || 'jpeg';
            return `https://cdn.bsky.app/img/feed_fullsize/plain/${adminDid}/${cid}@${ext}`;
        }
    }
    return studio.logoUrl || '';
};

export const isAdmin = (): boolean => {
    if (!currentUser.value?.handle) return false;
    const adminHandle = BSKY_ADMIN.replace(/^@/, '');
    return currentUser.value.handle.replace(/^@/, '') === adminHandle;
};

let _adminDid: string | null = null;
export const getAdminDid = async (): Promise<string> => {
    if (_adminDid) return _adminDid;
    const profile = await publicAgent.getProfile({ actor: BSKY_ADMIN });
    _adminDid = profile.data.did;
    return _adminDid;
};

export const getStudios = async (): Promise<Studio[]> => {
    try {
        const adminDid = await getAdminDid();
        const pdsUrl = await getPdsEndpoint(adminDid) || 'https://bsky.social';
        const pdsAgent = new BskyAgent({ service: pdsUrl });
        const response = await pdsAgent.com.atproto.repo.listRecords({
            repo: adminDid,
            collection: 'id.gamedev.studios',
            limit: 100
        });
        return response.data.records.map((r: any) => ({
            ...r.value,
            uri: r.uri,
            cid: r.cid,
            rkey: r.uri.split('/').pop()
        })) as Studio[];
    } catch (e) {
        console.error('[Studios] Failed to fetch studios:', e);
        return [];
    }
};

export const createStudio = async (data: Omit<Studio, 'uri' | 'cid' | 'rkey' | 'createdAt'>): Promise<void> => {
    if (!currentUser.value?.did) throw new Error('Not logged in');
    const record = {
        $type: 'id.gamedev.studios',
        ...data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
    };
    await _pdsAgent.com.atproto.repo.createRecord({
        repo: currentUser.value.did,
        collection: 'id.gamedev.studios',
        record
    });
};

export const updateStudio = async (rkey: string, data: Omit<Studio, 'uri' | 'cid' | 'rkey' | 'createdAt'>): Promise<void> => {
    if (!currentUser.value?.did) throw new Error('Not logged in');
    const record = {
        $type: 'id.gamedev.studios',
        ...data,
        updatedAt: new Date().toISOString()
    };
    await _pdsAgent.com.atproto.repo.putRecord({
        repo: currentUser.value.did,
        collection: 'id.gamedev.studios',
        rkey,
        record
    });
};

export const deleteStudio = async (rkey: string): Promise<void> => {
    if (!currentUser.value?.did) throw new Error('Not logged in');
    await _pdsAgent.com.atproto.repo.deleteRecord({
        repo: currentUser.value.did,
        collection: 'id.gamedev.studios',
        rkey
    });
};

// ── Pinned Info (id.gamedev.pinnedinfo) ──────────────────────────────────────

export interface PinnedInfo {
    uri?: string;
    cid?: string;
    rkey?: string;
    title: string;
    message: string;
    type: 'info' | 'warning' | 'success' | 'error';
    active: boolean;
    createdAt: string;
    updatedAt?: string;
}

export const getPinnedInfos = async (): Promise<PinnedInfo[]> => {
    try {
        const adminDid = await getAdminDid();
        const pdsUrl = await getPdsEndpoint(adminDid) || 'https://bsky.social';
        const pdsAgent = new BskyAgent({ service: pdsUrl });
        const response = await pdsAgent.com.atproto.repo.listRecords({
            repo: adminDid,
            collection: 'id.gamedev.pinnedinfo',
            limit: 100
        });
        return response.data.records.map((r: any) => ({
            ...r.value,
            uri: r.uri,
            cid: r.cid,
            rkey: r.uri.split('/').pop()
        })) as PinnedInfo[];
    } catch (e) {
        console.error('[PinnedInfo] Failed to fetch:', e);
        return [];
    }
};

export const getActivePinnedInfos = async (): Promise<PinnedInfo[]> => {
    const all = await getPinnedInfos();
    return all.filter(p => p.active);
};

export const createPinnedInfo = async (data: Omit<PinnedInfo, 'uri' | 'cid' | 'rkey' | 'createdAt'>): Promise<void> => {
    if (!currentUser.value?.did) throw new Error('Not logged in');
    await _pdsAgent.com.atproto.repo.createRecord({
        repo: currentUser.value.did,
        collection: 'id.gamedev.pinnedinfo',
        record: {
            $type: 'id.gamedev.pinnedinfo',
            ...data,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        }
    });
};

export const updatePinnedInfo = async (rkey: string, data: Omit<PinnedInfo, 'uri' | 'cid' | 'rkey' | 'createdAt'>): Promise<void> => {
    if (!currentUser.value?.did) throw new Error('Not logged in');
    await _pdsAgent.com.atproto.repo.putRecord({
        repo: currentUser.value.did,
        collection: 'id.gamedev.pinnedinfo',
        rkey,
        record: {
            $type: 'id.gamedev.pinnedinfo',
            ...data,
            updatedAt: new Date().toISOString()
        }
    });
};

export const deletePinnedInfo = async (rkey: string): Promise<void> => {
    if (!currentUser.value?.did) throw new Error('Not logged in');
    await _pdsAgent.com.atproto.repo.deleteRecord({
        repo: currentUser.value.did,
        collection: 'id.gamedev.pinnedinfo',
        rkey
    });
};

// ── Ads (id.gamedev.ads) ─────────────────────────────────────────────────────

export interface Ad {
    uri?: string;
    cid?: string;
    rkey?: string;
    link: string;
    imageBlob?: any;
    active: boolean;
    createdAt: string;
    updatedAt?: string;
}

export const resolveAdImageUrl = (ad: Ad, adminDid: string): string => {
    if (ad.imageBlob) {
        const cid = ad.imageBlob.ref?.$link || ad.imageBlob.ref?.toString() || ad.imageBlob.cid;
        if (cid && adminDid) {
            const ext = ad.imageBlob.mimeType?.split('/').pop() || 'jpeg';
            return `https://cdn.bsky.app/img/feed_fullsize/plain/${adminDid}/${cid}@${ext}`;
        }
    }
    return '';
};

export const getAds = async (): Promise<Ad[]> => {
    try {
        const adminDid = await getAdminDid();
        const pdsUrl = await getPdsEndpoint(adminDid) || 'https://bsky.social';
        const pdsAgent = new BskyAgent({ service: pdsUrl });
        const response = await pdsAgent.com.atproto.repo.listRecords({
            repo: adminDid,
            collection: 'id.gamedev.ads',
            limit: 100
        });
        return response.data.records.map((r: any) => ({
            ...r.value,
            uri: r.uri,
            cid: r.cid,
            rkey: r.uri.split('/').pop()
        })) as Ad[];
    } catch (e) {
        console.error('[Ads] Failed to fetch:', e);
        return [];
    }
};

export const getActiveAds = async (): Promise<Ad[]> => {
    const all = await getAds();
    return all.filter(a => a.active);
};

export const createAd = async (data: { link: string; imageBlob?: any; active: boolean }): Promise<void> => {
    if (!currentUser.value?.did) throw new Error('Not logged in');
    await _pdsAgent.com.atproto.repo.createRecord({
        repo: currentUser.value.did,
        collection: 'id.gamedev.ads',
        record: {
            $type: 'id.gamedev.ads',
            ...data,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        }
    });
};

export const updateAd = async (rkey: string, data: { link: string; imageBlob?: any; active: boolean }): Promise<void> => {
    if (!currentUser.value?.did) throw new Error('Not logged in');
    await _pdsAgent.com.atproto.repo.putRecord({
        repo: currentUser.value.did,
        collection: 'id.gamedev.ads',
        rkey,
        record: {
            $type: 'id.gamedev.ads',
            ...data,
            updatedAt: new Date().toISOString()
        }
    });
};

export const deleteAd = async (rkey: string): Promise<void> => {
    if (!currentUser.value?.did) throw new Error('Not logged in');
    await _pdsAgent.com.atproto.repo.deleteRecord({
        repo: currentUser.value.did,
        collection: 'id.gamedev.ads',
        rkey
    });
};

// ─────────────────────────────────────────────────────────────────────────────

export interface AppSettings { showTimeline: boolean; appTheme: 'dark' | 'light'; }

export const getAppSettings = async (): Promise<AppSettings | null> => {
    await initOAuth();
    if (!currentUser.value?.did) return null;
    try {
        const response = await _pdsAgent.com.atproto.repo.getRecord({
            repo: currentUser.value.did,
            collection: 'id.gamedev.settings',
            rkey: 'self'
        });
        return response.data.value as AppSettings;
    } catch (e: any) {
        if (e.status === 400 || e.status === 404 || e.error === 'RecordNotFound') return null;
        console.error('[AppSettings] Failed to load:', e);
        return null;
    }
};

export const saveAppSettings = async (settings: AppSettings): Promise<void> => {
    if (!currentUser.value?.did) throw new Error('Not logged in');
    await _pdsAgent.com.atproto.repo.putRecord({
        repo: currentUser.value.did,
        collection: 'id.gamedev.settings',
        rkey: 'self',
        record: {
            $type: 'id.gamedev.settings',
            showTimeline: settings.showTimeline,
            appTheme: settings.appTheme,
            updatedAt: new Date().toISOString()
        }
    });
};

// ─────────────────────────────────────────────────────────────────────────────

export const showAlert = (message: string, title: string = 'Notice') => {
    return new Promise<void>((resolve) => {
        globalPrompt.value = { show: true, title, message, type: 'alert', confirmText: 'OK', resolve: () => resolve() };
    });
};

export const showConfirm = (message: string, title: string = 'Confirmation') => {
    return new Promise<boolean>((resolve) => {
        globalPrompt.value = { show: true, title, message, type: 'confirm', confirmText: 'Confirm', cancelText: 'Cancel', resolve: (val) => resolve(val) };
    });
};

export const handlePromptResolve = (result: boolean) => {
    if (globalPrompt.value.resolve) { globalPrompt.value.resolve(result); }
    globalPrompt.value.show = false;
};
